import { useEffect } from "react";
import {
  clearPendingNotificationLink,
  getInAppPath,
  isPendingNotificationLinkFresh,
  loadAppPage,
  NOTIFICATION_CLICK_MESSAGE_TYPE,
  takePendingNotificationLink,
} from "../utilities/notificationClickUtility";

/**
 * When the app starts or comes to the front it looks for a tapped notification's link this
 * long after: iOS often brings the app back first and lets the service worker handle the tap
 * a moment later.
 */
export const PENDING_NOTIFICATION_CHECK_DELAYS_MS = [0, 1000, 3000];

interface INotificationClickMessage {
  type: typeof NOTIFICATION_CLICK_MESSAGE_TYPE;
  url: string;
}

const isNotificationClickMessage = (data: unknown): data is INotificationClickMessage =>
  typeof data === "object" &&
  data !== null &&
  (data as INotificationClickMessage).type === NOTIFICATION_CLICK_MESSAGE_TYPE &&
  typeof (data as INotificationClickMessage).url === "string";

/**
 * Opens the link of a tapped notification in the app (see public/service-worker.js). The
 * worker asks an open app by message; it also leaves the link in Cache Storage, which the app
 * checks when it starts or comes to the front, because iOS often loses the message or starts
 * the app before the worker can reach it. The page is loaded in full (see loadAppPage).
 */
export const useNotificationClickNavigation = () => {
  useEffect(() => {
    const serviceWorker = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!serviceWorker) {
      // No service worker, no notifications to open.
      return undefined;
    }

    let isActive = true;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const openPendingLink = async () => {
      const link = await takePendingNotificationLink();
      if (!isActive || !link || !isPendingNotificationLinkFresh(link, Date.now())) {
        return;
      }
      const path = getInAppPath(link.url, window.location.origin);
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      // The worker may already have opened this window on the link.
      if (path && path !== currentPath) {
        loadAppPage(path);
      }
    };

    const checkPendingLinkSoon = () => {
      PENDING_NOTIFICATION_CHECK_DELAYS_MS.forEach((delay) => {
        timers.push(setTimeout(() => void openPendingLink(), delay));
      });
    };

    const onMessage = async (event: MessageEvent) => {
      if (!isNotificationClickMessage(event.data)) {
        return;
      }
      const path = getInAppPath(event.data.url, window.location.origin);
      if (!path) {
        return;
      }
      // Tells the worker the app took the link, so it does not load it itself.
      event.ports?.[0]?.postMessage({ received: true });
      // Cleared first, so the loaded page does not open it a second time. Loaded even when
      // the app already shows that page: the tap is a new link for it to act on.
      await clearPendingNotificationLink();
      loadAppPage(path);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkPendingLinkSoon();
      }
    };

    serviceWorker.addEventListener("message", onMessage as EventListener);
    // Messages that arrived while the app was starting are delivered from here on.
    serviceWorker.startMessages?.();
    document.addEventListener("visibilitychange", onVisibilityChange);
    checkPendingLinkSoon();

    return () => {
      isActive = false;
      timers.forEach(clearTimeout);
      serviceWorker.removeEventListener("message", onMessage as EventListener);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
};
